"use client"

import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { auth, db } from "../firebase"
import { ref, onValue, update, get } from "../lib/firebase-database"
import { OwnerMobileNavbar } from "./OwnerMobileNavbar"
import { AlertTriangle, Check, X, MessageSquare, ArrowLeft } from 'lucide-react'
import { Button } from "./ui/button"
import { useAuth } from "../hooks/useAuth"

export function CancellationRequests() {
  const [requests, setRequests] = useState([])
  const [loading, setLoading] = useState(true)
  const [selectedRequest, setSelectedRequest] = useState(null)
  const [showDetailsDialog, setShowDetailsDialog] = useState(false)
  const user = auth.currentUser
  const navigate = useNavigate()
  const { isOwner } = useAuth()

  useEffect(() => {
    if (!user) {
      navigate("/login")
      return
    }

    if (!isOwner) {
      navigate("/")
      return
    }

    // Fetch cancellation requests
    const requestsRef = ref(db, "cancellationRequests")
    const unsubscribe = onValue(requestsRef, async (snapshot) => {
      const data = snapshot.val()
      if (!data) {
        setRequests([])
        setLoading(false)
        return
      }

      // Convert to array and add id
      const requestsArray = await Promise.all(
        Object.entries(data).map(async ([id, request]) => {
          // Get villa details if not already included
          let villaData = request.bookingData?.villaData
          if (!villaData && request.bookingData?.villaId) {
            const villaRef = ref(db, `villas/${request.bookingData.villaId}`)
            const villaSnapshot = await get(villaRef)
            if (villaSnapshot.exists()) {
              villaData = villaSnapshot.val()
            }
          }

          return {
            id,
            ...request,
            bookingData: {
              ...request.bookingData,
              villaData: villaData || {
                name: "Villa Name Not Available",
                thumbnail: "/placeholder.svg",
                address: "Address not available",
              },
            },
          }
        }),
      )

      // Sort by date (newest first)
      requestsArray.sort((a, b) => b.requestedAt - a.requestedAt)

      setRequests(requestsArray)
      setLoading(false)
    })

    return () => unsubscribe()
  }, [user, navigate, isOwner])

  const formatDate = (timestamp, timing) => {
    if (!timestamp) return "N/A";
    const date = new Date(timestamp);
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    
    const formattedDate = `${days[date.getDay()]}, ${months[date.getMonth()]} ${date.getDate()}, ${String(date.getFullYear()).slice(-2)}`;
    return timing ? `${formattedDate} (${timing})` : formattedDate;
  };

  const handleViewDetails = (request) => {
    setSelectedRequest(request)
    setShowDetailsDialog(true)
  }

  const handleApproveRequest = async (requestId) => {
    if (!requestId || !user) return

    try {
      const request = requests.find((r) => r.id === requestId)
      if (!request) return

      // Update cancellation request status
      await update(ref(db, `cancellationRequests/${requestId}`), {
        status: "approved",
        approvedAt: Date.now(),
        approvedBy: user.uid,
      })

      // Update booking status in user's bookings
      if (request.userId && request.bookingData?.id) {
        await update(ref(db, `bookings/${request.userId}/${request.bookingData.id}`), {
          status: "cancelled",
          cancelledAt: Date.now(),
          cancelledBy: user.uid,
          cancellationApproved: true,
          cancellationApprovedAt: Date.now(),
        })
      }

      // Update booking status in owner's bookings
      if (request.bookingData?.id) {
        await update(ref(db, `bookings/${user.uid}/${request.bookingData.id}`), {
          status: "cancelled",
          cancelledAt: Date.now(),
          cancelledBy: user.uid,
          cancellationApproved: true,
          cancellationApprovedAt: Date.now(),
        })
      }

      setShowDetailsDialog(false)
    } catch (error) {
      console.error("Error approving cancellation request:", error)
      alert("Failed to approve cancellation request. Please try again.")
    }
  }

  const handleRejectRequest = async (requestId) => {
    if (!requestId || !user) return

    try {
      const request = requests.find((r) => r.id === requestId)
      if (!request) return

      // Update cancellation request status
      await update(ref(db, `cancellationRequests/${requestId}`), {
        status: "rejected",
        rejectedAt: Date.now(),
        rejectedBy: user.uid,
      })

      // Update booking in user's bookings
      if (request.userId && request.bookingData?.id) {
        await update(ref(db, `bookings/${request.userId}/${request.bookingData.id}`), {
          cancellationRequested: false,
          cancellationRejected: true,
          cancellationRejectedAt: Date.now(),
        })
      }

      setShowDetailsDialog(false)
    } catch (error) {
      console.error("Error rejecting cancellation request:", error)
      alert("Failed to reject cancellation request. Please try again.")
    }
  }

  const handleChatWithUser = (request) => {
    if (!request || !request.userId || !request.bookingData?.villaId) return;
    navigate(`/chat/${request.bookingData.villaId}/${request.userId}`);
  }

  return (
    <div className="min-h-screen bg-gray-50 md:bg-gray-100 pb-16">
      <header className="bg-white shadow-sm md:shadow">
        <div className="max-w-7xl mx-auto py-4 md:py-6 px-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <button
                onClick={() => navigate(-1)}
                className="md:hidden p-1 rounded-full hover:bg-gray-100"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
              <h1 className="text-xl md:text-2xl font-bold text-gray-900">Cancellation Requests</h1>
            </div>
            <button
              onClick={() => navigate(-1)}
              className="hidden md:block px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Back
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto py-4 md:py-6 px-3 md:px-6">
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
          </div>
        ) : requests.length > 0 ? (
          <div className="grid gap-4 md:gap-6 md:grid-cols-2 lg:grid-cols-3">
            {requests.map((request) => (
              <div
                key={request.id}
                className={`bg-white rounded-lg overflow-hidden shadow-md hover:shadow-lg transition-all ${
                  request.status === "approved"
                    ? "border-l-4 border-green-500"
                    : request.status === "rejected"
                      ? "border-l-4 border-red-500"
                      : "border-l-4 border-yellow-500"
                }`}
              >
                <div className="relative h-32 md:h-40">
                  <img
                    src={request.bookingData?.villaData?.thumbnail || "/placeholder.svg"}
                    alt={request.bookingData?.villaData?.name}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent flex flex-col justify-end">
                    <div className="p-3 md:p-4 w-full">
                      <h3 className="text-white text-lg md:text-xl font-bold">
                        {request.bookingData?.villaData?.name || "Unknown Villa"}
                      </h3>
                    </div>
                  </div>
                </div>

                <div className="p-3 md:p-4">
                  <div className="flex justify-between mb-2 md:mb-3">
                    <div>
                      <p className="font-semibold text-gray-800 text-sm md:text-base">{request.userName || "Guest"}</p>
                      <p className="text-xs text-gray-500">Requested on: {formatDate(request.requestedAt)}</p>
                    </div>
                    <div>
                      {request.status === "approved" ? (
                        <span className="bg-green-100 text-green-800 text-xs font-medium px-2 py-0.5 rounded">
                          Approved
                        </span>
                      ) : request.status === "rejected" ? (
                        <span className="bg-red-100 text-red-800 text-xs font-medium px-2 py-0.5 rounded">
                          Rejected
                        </span>
                      ) : (
                        <span className="bg-yellow-100 text-yellow-800 text-xs font-medium px-2 py-0.5 rounded">
                          Pending
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="mb-2 md:mb-3">
                    <p className="text-xs md:text-sm text-gray-700 font-medium">Booking Details:</p>
                    <div className="grid grid-cols-2 gap-1 text-xs">
                      <p className="text-gray-600">Check-in:</p>
                      <p className="text-gray-800">{formatDate(request.bookingData?.checkIn)}</p>
                      <p className="text-gray-600">Check-out:</p>
                      <p className="text-gray-800">{formatDate(request.bookingData?.checkOut)}</p>
                      <p className="text-gray-600">Booking ID:</p>
                      <p className="text-gray-800 font-mono">{request.bookingData?.bookingId?.slice(-6) || "N/A"}</p>
                    </div>
                  </div>

                  <div className="mb-3 md:mb-4">
                    <p className="text-xs md:text-sm text-gray-700 font-medium">Cancellation Reason:</p>
                    <p className="text-xs md:text-sm text-gray-600 line-clamp-2">{request.reason}</p>
                  </div>

                  <div className="flex gap-2">
                    <Button
                      onClick={() => handleViewDetails(request)}
                      className="flex-1 bg-blue-500 hover:bg-blue-600 text-white text-xs md:text-sm py-2"
                    >
                      View Details
                    </Button>
                    {request.status === "pending" && (
                      <Button
                        onClick={() => handleChatWithUser(request)}
                        className="w-9 h-9 md:w-10 md:h-10 p-0 bg-gray-200 hover:bg-gray-300 text-gray-700"
                      >
                        <MessageSquare className="h-4 w-4 md:h-5 md:w-5" />
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 md:py-12">
            <AlertTriangle className="h-10 w-10 md:h-12 md:w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500 text-base md:text-lg">No cancellation requests found.</p>
          </div>
        )}
      </main>

      {showDetailsDialog && selectedRequest && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg p-4 md:p-6 max-w-md w-full">
            <div className="flex items-center justify-between mb-3 md:mb-4">
              <h3 className="text-base md:text-lg font-semibold">Cancellation Request Details</h3>
              <button onClick={() => setShowDetailsDialog(false)} className="text-gray-500 hover:text-gray-700">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-3 md:space-y-4 text-sm md:text-base">
              <div>
                <p className="text-xs md:text-sm text-gray-500">Villa</p>
                <p className="font-medium">{selectedRequest.bookingData?.villaData?.name || "Unknown Villa"}</p>
              </div>

              <div>
                <p className="text-xs md:text-sm text-gray-500">Guest</p>
                <p className="font-medium">{selectedRequest.userName || "Guest"}</p>
              </div>

              <div>
                <p className="text-xs md:text-sm text-gray-500">Booking ID</p>
                <p className="font-mono font-medium">{selectedRequest.bookingData?.bookingId || "Unknown"}</p>
              </div>

              <div className="grid grid-cols-2 gap-3 md:gap-4">
                <div>
                  <p className="text-xs md:text-sm text-gray-500">Check-in</p>
                  <p className="font-medium text-xs md:text-sm">
                    {formatDate(selectedRequest.bookingData?.checkIn)} ({selectedRequest.bookingData?.checkInTiming || 'Morning'})
                  </p>
                </div>
                <div>
                  <p className="text-xs md:text-sm text-gray-500">Check-out</p>
                  <p className="font-medium text-xs md:text-sm">
                    {formatDate(selectedRequest.bookingData?.checkOut)} ({selectedRequest.bookingData?.checkOutTiming || 'Morning'})
                  </p>
                </div>
              </div>

              <div>
                <p className="text-xs md:text-sm text-gray-500">Cancellation Reason</p>
                <div className="mt-1 p-2 md:p-3 bg-gray-50 rounded-md">
                  <p className="text-xs md:text-sm text-gray-700">{selectedRequest.reason}</p>
                </div>
              </div>

              <div>
                <p className="text-xs md:text-sm text-gray-500">Request Status</p>
                <p className="font-medium">
                  {selectedRequest.status === "approved"
                    ? "Approved"
                    : selectedRequest.status === "rejected"
                      ? "Rejected"
                      : "Pending"}
                </p>
              </div>

              {selectedRequest.status === "pending" && (
                <div className="flex gap-2 pt-2">
                  <Button
                    onClick={() => handleRejectRequest(selectedRequest.id)}
                    className="flex-1 bg-gray-500 hover:bg-gray-600 text-white text-xs md:text-sm py-2"
                  >
                    <X className="h-3 w-3 md:h-4 md:w-4 mr-1" />
                    Reject
                  </Button>
                  <Button
                    onClick={() => handleApproveRequest(selectedRequest.id)}
                    className="flex-1 bg-green-500 hover:bg-green-600 text-white text-xs md:text-sm py-2"
                  >
                    <Check className="h-3 w-3 md:h-4 md:w-4 mr-1" />
                    Approve
                  </Button>
                </div>
              )}

              <Button
                onClick={() => {
                  if (selectedRequest) {
                    navigate(`/chat/${selectedRequest.bookingData.villaId}/${selectedRequest.userId}`);
                    setShowDetailsDialog(false);
                  }
                }}
                className="w-full bg-blue-500 hover:bg-blue-600 text-white text-xs md:text-sm py-2"
              >
                <MessageSquare className="h-3 w-3 md:h-4 md:w-4 mr-1" />
                Chat with User
              </Button>
            </div>
          </div>
        </div>
      )}

      <OwnerMobileNavbar user={user} />
    </div>
  )
}

export default CancellationRequests
